import { singleton } from "tsyringe";
import {
    formatISO,
    parseISO,
    format,
    addWeeks,
    subWeeks,
    differenceInDays,
    differenceInWeeks,
    set,
    startOfDay as fnStartOfDay,
    formatDistanceToNow,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameDay,
    isSameMonth,
    isToday,
    subMonths,
    addMonths,
    addDays,
    StartOfWeekOptions,
    EndOfWeekOptions,
    StartOfMonthOptions,
    EndOfMonthOptions,
} from "date-fns";
import { ko } from "date-fns/locale";

import { TZDate } from "@date-fns/tz";

import type {
    IDateAdapter,
    ParseISOAdapterOptions,
    FormatISOAdapterOptions,
    FormatDateAdapterOptions,
} from "@/shared/libs/date/interface";

@singleton()
export default class DateFns implements IDateAdapter {
    constructor() {}

    public parseISO = (date: string, options?: ParseISOAdapterOptions): Date => {
        return parseISO(date, options?.fn);
    };

    public formatISO = (date: Date, options?: FormatISOAdapterOptions): string => {
        return formatISO(date, options?.fn);
    };

    public parseZoned = (date: Date, timeZone: string): Date => {
        return new TZDate(date, timeZone);
    };

    public formatZoned = (
        date: Date,
        timeZone: string,
        formatStr?: string,
        options?: FormatDateAdapterOptions,
    ): string => {
        const dateWithTimeZone = new TZDate(date, timeZone);

        return format(dateWithTimeZone, formatStr ?? "yyyy-MM-dd HH:mm:ss", options?.fn);
    };

    public addWeeks = (date: Date, weeks: number): Date => {
        return addWeeks(date, weeks);
    };

    public subWeeks = (date: Date, weeks: number): Date => {
        return subWeeks(date, weeks);
    };

    public differenceInDays = (dateLeft: Date, dateRight: Date): number => {
        return differenceInDays(dateLeft, dateRight);
    };

    public differenceInWeeks = (dateLeft: Date, dateRight: Date): number => {
        return differenceInWeeks(dateLeft, dateRight);
    };

    public setHours = (
        date: Date,
        hours: number,
        minutes?: number,
        seconds?: number,
        milliseconds?: number,
    ): Date => {
        return set(date, { hours, minutes, seconds, milliseconds });
    };

    public formatQueryDate = (date: Date): string => {
        const setTimezone = new TZDate(date, "Asia/Seoul");
        const formattedISODate = formatISO(setTimezone);

        return encodeURIComponent(formattedISODate);
    };

    /**
     * 날짜의 시간을 00:00:00으로 설정합니다.
     *
     * @param date 기준 날짜
     * @param timeZone 시간대
     * @returns 시간이 00:00:00으로 설정된 날짜
     */
    public startOfDay = (date: Date, timeZone: string): Date => {
        // 먼저 지정된 시간대로 날짜 변환
        const zonedDate = new TZDate(date, timeZone);

        // 해당 날짜의 시작 시간(00:00:00)으로 설정
        const startOfDayDate = fnStartOfDay(zonedDate);

        // 시간대 정보를 유지한 날짜 반환
        return new TZDate(startOfDayDate, timeZone);
    };

    /**
     * 두 날짜 사이의 일수 차이를 계산합니다. 시간 정보는 무시합니다.
     *
     * @param dateLeft 첫 번째 날짜
     * @param dateRight 두 번째 날짜
     * @param timeZone 시간대
     * @returns 일수 차이 (날짜만 고려)
     */
    public differenceInDaysIgnoringTime = (
        dateLeft: Date,
        dateRight: Date,
        timeZone: string,
    ): number => {
        // 각 날짜의 시작 시간(00:00:00)으로 설정
        const startOfDayLeft = this.startOfDay(dateLeft, timeZone);
        const startOfDayRight = this.startOfDay(dateRight, timeZone);

        // 밀리초 단위로 차이 계산
        const diffInMilliseconds = startOfDayLeft.getTime() - startOfDayRight.getTime();

        // 일 단위로 변환 (1일 = 24시간 = 86400000 밀리초)
        return Math.floor(diffInMilliseconds / 86400000);
    };

    public formatRelativeDateIn24Hours = (date: Date, timezone: string): string => {
        const now = new TZDate(new Date(), timezone);
        const targetDate = new TZDate(date, timezone);

        if (isNaN(targetDate.getTime())) {
            throw new Error("Invalid date input");
        }

        const diffMs = now.getTime() - targetDate.getTime();
        const oneDay = 24 * 60 * 60 * 1000;

        // 만일 차이가 24시간 이내라면 relative 로 표시되도록 합니다.
        if (diffMs >= 0 && diffMs < oneDay) {
            return formatDistanceToNow(targetDate, { addSuffix: true, locale: ko });
        } else {
            // 그 이후는 yy.mm.dd 로 표시되도록 합니다.
            return format(targetDate, "yy.MM.dd", { locale: ko });
        }
    };

    /**
     * @description
     * Date 시간을 HH:mm 형식으로 변환하면서, 접두사로 오전, 오후가 표시됩니다. (한국 기준)
     * @param date target date
     * @param timezone target time zone
     * @returns {string} formatted date 입니다.
     */
    public formatTimeWithMeridiem = (date: Date, timezone: string = "Asia/Seoul"): string => {
        const zonedDate = new TZDate(date, timezone);
        return format(zonedDate, "a HH:mm", { locale: ko });
    };

    /**
     * @description
     * Date 시간을 yy.mm.dd HH:mm:ss 형식으로 반환합니다.
     * @param date
     * @param timezone
     * @returns
     */
    public formatFullYearDateAndTime = (date: Date, timezone: string = "Asia/Seoul"): string => {
        const zonedDate = new TZDate(date, timezone);
        return format(zonedDate, "yyyy.MM.dd HH:mm:ss", { locale: ko });
    };

    public firstMondayOfMonthRange = (date: Date, timezone?: string): Date => {
        const zonedDate = new TZDate(date, timezone ?? "Asia/Seoul");
        const monthStart = startOfMonth(zonedDate);

        const firstMonday = startOfWeek(monthStart, { weekStartsOn: 1 });

        return firstMonday;
    };

    public lastDayOfMonthRange = (date: Date, timezone?: string): Date => {
        const zonedDate = new TZDate(date, timezone ?? "Asia/Seoul");
        const monthEnd = endOfMonth(zonedDate);

        const lastDay = endOfWeek(monthEnd, { weekStartsOn: 1 });

        return lastDay;
    };

    public getDatesBetween = (startDate: Date, endDate: Date, timezone?: string): Date[] => {
        const zonedStartDate = new TZDate(startDate, timezone ?? "Asia/Seoul");
        const zonedEndDate = new TZDate(endDate, timezone ?? "Asia/Seoul");

        return eachDayOfInterval({ start: zonedStartDate, end: zonedEndDate });
    };

    public isSameDate = (date1: Date, date2: Date, timezone?: string): boolean => {
        const zonedDate1 = new TZDate(date1, timezone ?? "Asia/Seoul");
        const zonedDate2 = new TZDate(date2, timezone ?? "Asia/Seoul");

        return isSameDay(zonedDate1, zonedDate2);
    };

    public isSameMonth = (date1: Date, date2: Date, timezone?: string): boolean => {
        const zonedDate1 = new TZDate(date1, timezone ?? "Asia/Seoul");
        const zonedDate2 = new TZDate(date2, timezone ?? "Asia/Seoul");

        return isSameMonth(zonedDate1, zonedDate2);
    };

    public isToday = (date: Date, timezone?: string): boolean => {
        const zonedDate = new TZDate(date, timezone ?? "Asia/Seoul");

        return isToday(zonedDate);
    };

    public subMonths = (date: Date, months: number, timezone?: string): Date => {
        const zonedDate = new TZDate(date, timezone ?? "Asia/Seoul");
        return subMonths(zonedDate, months);
    };

    public addMonths = (date: Date, months: number, timezone?: string): Date => {
        const zonedDate = new TZDate(date, timezone ?? "Asia/Seoul");
        return addMonths(zonedDate, months);
    };

    public startOfWeek = (date: Date, options?: StartOfWeekOptions, timezone?: string): Date => {
        const zonedDate = new TZDate(date, timezone ?? "Asia/Seoul");
        return startOfWeek(zonedDate, options);
    };

    public endOfWeek = (date: Date, options?: EndOfWeekOptions, timezone?: string): Date => {
        const zonedDate = new TZDate(date, timezone ?? "Asia/Seoul");
        return endOfWeek(zonedDate, options);
    };

    public startOfMonth = (
        date: Date,
        options?: StartOfMonthOptions<Date>,
        timezone?: string,
    ): Date => {
        const zonedDate = new TZDate(date, timezone ?? "Asia/Seoul");
        return startOfMonth(zonedDate, options);
    };

    public endOfMonth = (
        date: Date,
        options?: EndOfMonthOptions<Date>,
        timezone?: string,
    ): Date => {
        const zonedDate = new TZDate(date, timezone ?? "Asia/Seoul");
        return endOfMonth(zonedDate, options);
    };

    public addDays = (date: Date, days: number, timezone?: string): Date => {
        const zonedDate = new TZDate(date, timezone ?? "Asia/Seoul");
        return addDays(zonedDate, days);
    };
}
